;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { format, parseISO } from "date-fns";
import type { BaseChartProps } from "@/components/charts/base-chart";
import type { DateRangeInput } from "@/lib/utils/date-parser";

type DateRangeLabelValue =
	| DateRangeInput
	| {
			endDate?: string | Date;
			startDate?: string | Date;
	  }
	| null
	| undefined;

export const formatDate = (date: string | Date, formatStr = "PPP"): string => {
	const parsedDate = typeof date === "string" ? parseISO(date) : date;
	return format(parsedDate, formatStr);
};

export const formatDateTime = (date: string | Date): string => {
	return formatDate(date, "PPpp");
};

export const formatTime = (date: string | Date): string => {
	return formatDate(date, "p");
};

export const isToday = (date: string | Date): boolean => {
	const today = new Date();
	const compareDate = typeof date === "string" ? parseISO(date) : date;
	return (
		compareDate.getDate() === today.getDate() &&
		compareDate.getMonth() === today.getMonth() &&
		compareDate.getFullYear() === today.getFullYear()
	);
};

function hasFormattableDate(value: unknown): value is string | Date {
	if (typeof value === "string") {
		return value.length > 0;
	}

	return value instanceof Date && !Number.isNaN(value.getTime());
}

export const renderDateRangeLabel = (
	dateRange: DateRangeLabelValue,
	action: Pick<
		BaseChartProps["actions"][number],
		"variant" | "options" | "dateRange"
	>,
): string => {
	const isDateRange =
		action?.variant === "date-range" &&
		dateRange !== null &&
		typeof dateRange === "object";

	if (isDateRange) {
		const granularity = action?.dateRange?.granularity ?? "day";
		const dateFormat = granularity === "month" ? "MMM yyyy" : "PP";
		const startLabel = hasFormattableDate(dateRange.startDate)
			? formatDate(dateRange.startDate, dateFormat)
			: null;
		const endLabel = hasFormattableDate(dateRange.endDate)
			? formatDate(dateRange.endDate, dateFormat)
			: null;

		if (startLabel && endLabel) {
			if (granularity === "month" && startLabel === endLabel) {
				return startLabel;
			}

			return `${startLabel} - ${endLabel}`;
		}

		return startLabel ?? endLabel ?? "Unknown Value";
	}

	// Handle string-based date ranges (e.g., "last-6m", "current-month")
	if (typeof dateRange === "string") {
		const dateRangeLabel = action?.options?.find(
			(option) => option.key === dateRange,
		)?.label;

		// If we found a matching option, return its label
		if (dateRangeLabel) {
			return dateRangeLabel;
		}

		// If no match found but it's a valid string, try to parse it as a known format
		// This handles cases where saved filters have old keys
		const dateRangeMap: Record<string, string> = {
			"3m": "Last 3 Months",
			"6m": "Last 6 Months",
			"12m": "Last 12 Months",
			all: "All Dates",
			"all-dates": "Any time",
			"current-month": "Current Month",
			"last-3m": "Last 3 Months",
			"last-6m": "Last 6 Months",
			"last-7d": "Last 7 Days",
			"last-12m": "Last 12 Months",
			"last-month": "Last Month",
			next_3m: "Next 3 Months",
			next_6m: "Next 6 Months",
			next_12m: "Next 12 Months",
			next_24m: "Next 24 Months",
			"next-month": "Next Month",
		};

		return dateRangeMap[dateRange] ?? "Custom Range";
	}

	return "Unknown Value";
};
